import gsap from 'gsap'
import { createContext, ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'

import Keyboard from '@/components/Keyboard'
import NumericKeyboard from '@/components/NumericKeyboard'

type KeyboardType = 'text' | 'numeric' | 'numericAndText'

interface KeyboardConfig {
  id: string
  type: KeyboardType
  value: string
  maxLength?: number
  onChange: (value: string) => void
  onEnter?: () => void
}

interface KeyboardContextValue {
  activeId: string | null
  closeKeyboard: () => void
  openKeyboard: (config: KeyboardConfig) => void
  scheduleCloseKeyboard: (id: string) => void
}

const KeyboardContext = createContext<KeyboardContextValue | null>(null)

const getModalDialog = () => document.querySelector<HTMLElement>('#modal [data-element="modal-dialog"]')

export const useKeyboardProvider = () => useContext(KeyboardContext)

const KEYBOARD_EXIT_DURATION = 220

export const KeyboardProvider = ({ children }: { children: ReactNode }) => {
  const [activeKeyboard, setActiveKeyboard] = useState<KeyboardConfig | null>(null)
  const [renderedKeyboard, setRenderedKeyboard] = useState<KeyboardConfig | null>(null)
  const [animateKeyboardIn, setAnimateKeyboardIn] = useState(true)
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const activeKeyboardRef = useRef<KeyboardConfig | null>(null)
  const renderedKeyboardRef = useRef<KeyboardConfig | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    activeKeyboardRef.current = activeKeyboard
  }, [activeKeyboard])

  useEffect(() => {
    renderedKeyboardRef.current = renderedKeyboard
  }, [renderedKeyboard])

  useEffect(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current)
      exitTimerRef.current = null
    }

    if (activeKeyboard) {
      setAnimateKeyboardIn(!renderedKeyboardRef.current)
      setRenderedKeyboard(activeKeyboard)
      return
    }

    exitTimerRef.current = setTimeout(() => {
      setRenderedKeyboard(null)
      renderedKeyboardRef.current = null
      exitTimerRef.current = null
    }, KEYBOARD_EXIT_DURATION)
  }, [activeKeyboard])

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const openKeyboard = useCallback((config: KeyboardConfig) => {
    clearCloseTimer()
    setActiveKeyboard(config)
  }, [])

  const closeKeyboard = useCallback(() => {
    clearCloseTimer()
    setActiveKeyboard(null)
    setKeyboardHeight(0)
  }, [])

  const scheduleCloseKeyboard = useCallback((id: string) => {
    clearCloseTimer()
    closeTimerRef.current = setTimeout(() => {
      if (activeKeyboardRef.current?.id === id) {
        setActiveKeyboard(null)
        setKeyboardHeight(0)
      }
    }, 80)
  }, [])

  useLayoutEffect(() => {
    const modalDialog = getModalDialog()
    if (!modalDialog) {
      return
    }

    gsap.to(modalDialog, {
      y: activeKeyboard && keyboardHeight ? -keyboardHeight / 2 : 0,
      duration: 0.18,
      ease: 'power2.out'
    })
  }, [activeKeyboard, keyboardHeight])

  useEffect(() => {
    return () => {
      clearCloseTimer()
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current)
      }
      const modalDialog = getModalDialog()
      if (modalDialog) {
        gsap.set(modalDialog, { y: 0 })
      }
    }
  }, [])

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const currentKeyboard = activeKeyboardRef.current
      if (!currentKeyboard) {
        return
      }

      const target = event.target
      if (!(target instanceof Element)) {
        return
      }

      if (target.closest('[data-element="keyboard-portal"]') || target.closest('[data-keyboard-input-id]') || target.closest('button')) {
        return
      }

      event.stopPropagation()
      event.preventDefault()
      closeKeyboard()

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown, true)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
    }
  }, [closeKeyboard])

  const handleKeyboardChange = (value: string) => {
    const currentKeyboard = activeKeyboardRef.current
    if (!currentKeyboard) {
      return
    }

    setActiveKeyboard({ ...currentKeyboard, value })
    currentKeyboard.onChange(value)
  }

  return (
    <KeyboardContext.Provider
      value={{
        activeId: activeKeyboard?.id ?? null,
        closeKeyboard,
        openKeyboard,
        scheduleCloseKeyboard
      }}
    >
      {children}
      {renderedKeyboard?.type === 'numeric' && (
        <NumericKeyboard
          visible={Boolean(activeKeyboard)}
          offsetDialog={false}
          animateOnMount={animateKeyboardIn}
          value={renderedKeyboard.value}
          onChange={handleKeyboardChange}
          maxLength={renderedKeyboard.maxLength}
          onHeightChange={setKeyboardHeight}
        />
      )}
      {renderedKeyboard?.type === 'text' && (
        <Keyboard
          visible={Boolean(activeKeyboard)}
          offsetDialog={false}
          animateOnMount={animateKeyboardIn}
          value={renderedKeyboard.value}
          onChange={handleKeyboardChange}
          maxLength={renderedKeyboard.maxLength}
          onEnter={renderedKeyboard.onEnter}
          onHeightChange={setKeyboardHeight}
        />
      )}
      {renderedKeyboard?.type === 'numericAndText' && (
        <Keyboard
          visible={Boolean(activeKeyboard)}
          offsetDialog={false}
          numeric
          animateOnMount={animateKeyboardIn}
          value={renderedKeyboard.value}
          onChange={handleKeyboardChange}
          maxLength={renderedKeyboard.maxLength}
          onEnter={renderedKeyboard.onEnter}
          onHeightChange={setKeyboardHeight}
        />
      )}
    </KeyboardContext.Provider>
  )
}
